import moment from 'moment'
import _ from 'lodash'

import { formatItineraryTitle } from './itinerary'

function formatDestionationsContent(destinations) {
  return _.reduce(destinations, (destinationMap, item) => ({
    ...destinationMap,
    [item.id]: item,
  }), {})
}

function constructDestinationsFromItinerary(itinerary, fallbackDestinations, destinationsContent) {
  if (_.isEmpty(itinerary)) {
    return _.map(fallbackDestinations, item => ({
      id: item,
      title: _.get(destinationsContent, `[${item}].name`)
    }))
  }
  return _.reduce(itinerary, (destinations, item) => {
    const updatedDestinations = _.cloneDeep(destinations)
    _.forEach(_.get(item, 'destinations', []), destinationItem => {
      if (_.get(destinationItem, 'destinationId') !== _.get(_.last(updatedDestinations), 'id')) {
        updatedDestinations.push({
          id: _.get(destinationItem, 'destinationId'),
          title: _.get(destinationsContent, `[${_.get(destinationItem, 'destinationId')}].name`)
        })
      }
    })
    return updatedDestinations
  }, [])
}

function getAccommodationsForItineraryKeyedByDestinationId(
  tripDestinations,
  accommodations,
) {
  const accommodationsByDestination = _.reduce(accommodations, (obj, item) => {
    const destinationId = _.get(item, 'destination.id')
    if (!obj[destinationId]) {
      return {
        ...obj,
        [destinationId]: [item],
      }
    }
    return {
      ...obj,
      [destinationId]: [...obj[destinationId], item],
    }
  }, {})
  return _.pick(accommodationsByDestination, tripDestinations)
}

function sortAccommodationsForItinerary(accommodations) {
  const featuredOnItinerary = []
  const featuredOther = []
  const remaining = []
  _.forEach(accommodations, item => {
    if (item.featuredOnItinerary) {
      featuredOnItinerary.push(item)
    } else if (item.featured) {
      featuredOther.push(item)
    } else {
      remaining.push(item)
    }
  })
  return _.concat(featuredOnItinerary, featuredOther, _.orderBy(remaining, ['viewExtensions.rating'], ['desc']))
}

export function getAllTourAccommodations(tourItinerary) {
  const uniqueAccomodations = _.uniqBy(_.flatMap(tourItinerary, item => _.get(item, 'accommodations', [])), 'id')
  return sortAccommodationsForItinerary(uniqueAccomodations)
}

export function addAccommodationsToTripItinerary(
  accommodations,
  tour
) {
  const tripItinerary = _.get(tour, 'tripItinerary')
  const accommodationIdsKeyedByDestination = getAccommodationsForItineraryKeyedByDestinationId(
    _.map(_.get(tour, 'destinations'), item => item.id),
    accommodations,
  )

  // for each itinerary item, if it has destinations,
  // add accommodations list for each destination
  _.forEach(_.get(tour, 'tripItinerary'), (itineraryItem, itineraryKey) => {
    const destinations = _.get(itineraryItem, 'destinations', [])
    if (!_.isEmpty(destinations)) {
      // YLG400-464: Accommodations can be hidden per destination based on a
      // flag mediated in from the CMS.
      const destinationIds = _.reduce(destinations, (idList, destination) => {
        return _.get(destination, 'hideHotels')
          ? idList
          : [...idList,  _.get(destination, 'destinationId')]
      }, [])

      const itineraryItemAccommodations = _.reduce(
        destinationIds,
        (accommodationsIdList, destinationId) => {
          let updatedIdList = _.cloneDeep(accommodationsIdList)
          if (!_.isEmpty(accommodationIdsKeyedByDestination[destinationId])) {
            updatedIdList = _.concat(
              updatedIdList,
              accommodationIdsKeyedByDestination[destinationId]
            )
          }
          return updatedIdList
        },
        []
      )
      const uniqueAccommodations = _.uniqBy(itineraryItemAccommodations, 'id')
      // YLG210-41: Featured accommodations need to be displayed first in the list
      tripItinerary[itineraryKey].accommodations = sortAccommodationsForItinerary(uniqueAccommodations)
    }
  })

  return tripItinerary
}

/**
 * An offer is an active flash sale if its isFlashSale flag is true,
 * it has a status value of true, and it meets the trip duration
 * requirements.
 */
function getActiveFlashSaleOffer(offers, numberOfNightsInTrip) {
  return _.find(offers, offer => {
    const isFlashSale = _.get(offer, 'flashSale')
    const offerStatus = _.get(offer, 'status')
    const requiredDuration = _.get(offer, 'requiredDuration')
      ? _.parseInt(_.get(offer, 'requiredDuration'))
      : 0
    return offerStatus && isFlashSale && numberOfNightsInTrip >= requiredDuration
  })
}

/**
 * We assume that if an offer description contains numeric digits, has a status
 * value of true, and meets the trip duration requirements, it is valid. Offers
 * are filtered based on that assumption, then all non-numeric characters are
 * stripped from the descriptions and the offers are sorted in descending order
 * by the discount amount.
 */
function getHighestDiscountOffer(offers, numberOfNightsInTrip) {
  const offersWithValidDiscounts = _.filter(offers,
    offer => {
      const requiredDuration = _.get(offer, 'requiredDuration')
        ? _.parseInt(_.get(offer, 'requiredDuration'))
        : 0
      return /\d/.test(_.get(offer, 'priceDescriptionPrimary'))
        && _.get(offer, 'status')
        && numberOfNightsInTrip >= requiredDuration
    }
  )
  const offersWithFormattedDiscounts = _.map(offersWithValidDiscounts, offer => {
    const offerDescription = _.get(offer, 'priceDescriptionPrimary')
    const discountValue = parseInt(offerDescription.replace(/\D/g,''), 10);
    return {
      ...offer,
      discountValue,
    }
  })
  const offersSortedByDiscountValue = _.orderBy(offersWithFormattedDiscounts, ['discountValue'], ['desc'])
  return offersSortedByDiscountValue[0]
}

/**
 * The priority offer is either the active flash sale offer
 * or the active offer with the highest discount.
 */
function formatOffer(offers, tripDuration) {
  // The softrip duration is determined by the number of days in the trip, while the
  // offer duration is based on the number of nights. We subtract 1 from the duration
  // in order to determine whether the trip fits the offer duration.
  const numberOfNightsInTrip = tripDuration - 1
  const flashSaleOffer = getActiveFlashSaleOffer(offers, numberOfNightsInTrip)
  let priorityOffer = flashSaleOffer
  if (!flashSaleOffer) {
    priorityOffer = getHighestDiscountOffer(offers, numberOfNightsInTrip)
  }
  return priorityOffer
    ? {
        bannerText: _.get(priorityOffer, 'flag') || _.get(priorityOffer, 'priceDescriptionPrimary'),
        description: _.get(priorityOffer, 'banner.description'),
        descriptionHeading: _.get(priorityOffer, 'banner.heading.text'),
        image: _.get(priorityOffer, 'image'),
        url: _.get(priorityOffer, 'link.generatedUrl'),
      }
    : null
}

export function formatItineraryDestinationsForDisplay(destinations) {
  const destinationTitles = _.map(destinations, (destination) =>
    _.get(destination, 'destinationTitle')
  )
  return _.join(destinationTitles, ' | ')
}

export function formatTourPricing(tour) {
  const pricing = Array.isArray(_.get(tour, 'pricing'))
    ? _.get(tour, 'pricing')
    : [_.get(tour, 'pricing')]
  return _.map(pricing, (pricingCategory, index) => {
    return {
      id: _.get(pricingCategory, 'viewExtensions.planNumber', index),
      title: _.get(pricingCategory, 'viewExtensions.planName', ''),
      pricing: _.reduce(
        _.get(pricingCategory, 'participantCategory', []),
        (tourPricing, date) => {
          const availDate = _.get(date, 'viewExtensions.date')
          if (moment(availDate).isAfter(moment(), 'day')) {
            return {
              ...tourPricing,
              [availDate]: {
                ..._.get(date, 'price'),
                ..._.get(date, 'viewExtensions'),
                qualifierInfo: _.get(date, 'qualifierInfo'),
              },
            }
          }
          return tourPricing
        },
        {}
      ),
    }
  })
}

export function formatTourPricingForMobileLayout(pricing) {
  return _.map(pricing, pricingCategory => {
    const initialPricing = _.get(pricingCategory, 'pricing')
    const pricingList = {}
    // assign dates to list keyed by month
    _.forEach(initialPricing, (info, date) => {
      const month = moment(date).format('YYYY-MM')
      if (info.saleStatus !== 'C') {
        if (pricingList[month]) {
          pricingList[month] = _.concat(pricingList[month], info)
        } else {
          pricingList[month] = [info]
        }
      }
    })
    // sort list for each month
    _.forEach(pricingList, (list, key) => {
      pricingList[key] = _.sortBy(pricingList[key], ['date'])
    })
    return {
      ...pricingCategory,
      pricing: pricingList
    }
  })
}

/**
 * Return index of next destination image to feature given previously featured image index.
 */
export function getNextFeaturedImageIndex(previousImageIndex, destinationImages) {
  let nextImageIndex = 0;
  if (!_.isNil(previousImageIndex)) {
    nextImageIndex = (previousImageIndex + 1) % destinationImages.length
  }
  return nextImageIndex;
}

/**
 * Return the itinerary object transformed into an array of itineraryItems with an added featured image field.
 * 
 * Each itinerary item for a given destination should use a unique image from the list of destination images if possible.
 * If there are fewer destination images than itineraryItems for that destination in a tour, every image should be used before repeating.
 * 
 * Amendment: If the itinerary heading includes "overnight onboard", we use an image of the train as the featured image.
 */
export function formatItineraryItems(itinerary, destinations) {
  const featuredImageIndicesByDestinationId = {}
  return _.forEach(itinerary, (item, key) => {
    const tourDayItineraryHeading = _.toLower(_.get(item, 'heading'))
    const isOvernightOnboardTrain = _.includes(tourDayItineraryHeading, 'overnight onboard')
    const overnightTrainImage = _.get(item, 'trains[0].images[0]')

    let featuredImage = null
    if (isOvernightOnboardTrain && overnightTrainImage) {
      featuredImage = overnightTrainImage
    } else {
      const destinationId = _.get(item, 'destinations[0].destinationId')
      const destinationImages = _.has(destinations, `[${destinationId}].viewExtensions.itineraryImages`)
        ? _.get(destinations, `[${destinationId}].viewExtensions.itineraryImages`, [])
        : _.has(destinations, `[${destinationId}].viewExtensions.image`)
        ? [_.get(destinations, `[${destinationId}].viewExtensions.image`)]
        : []
      const featuredImageIndex = getNextFeaturedImageIndex(featuredImageIndicesByDestinationId[destinationId], destinationImages)
      featuredImage = destinationImages[featuredImageIndex] || null
      featuredImageIndicesByDestinationId[destinationId] = featuredImageIndex
    }

    itinerary[key] = {
      ...item,
      featuredImage,
      heading: formatItineraryTitle(_.get(item, 'heading')),
    }
  })
}

export function formatTour(product, destinations) {
  const destinationsContent = formatDestionationsContent(destinations)

  const duration = _.get(product, 'viewExtensions.duration')
  const vacationTypes = _.map(_.get(product, 'viewExtensions.vacationTypes', []), item => _.replace(item.title, 'TripType_', ''))
  const isCityBreak = Boolean(_.find(vacationTypes, type => type.toLowerCase() === 'city break'))
  const tripDestinations = constructDestinationsFromItinerary(
    _.get(product, 'viewExtensions.itinerary'),
    _.get(product, 'viewExtensions.locationIds'),
    destinationsContent,
  )
  const destinationEnd = _.last(tripDestinations)
  const destinationStart = _.first(tripDestinations)
  const uniqueDestinations = _.uniq(_.map(tripDestinations, item => item.id))
  const tripItinerary = formatItineraryItems(_.get(product, 'viewExtensions.itinerary'), destinationsContent)

  return {
    id: _.get(product, 'id', ''),
    accommodations: _.get(product, 'viewExtensions.accommodationIds'),
    advertisedPrice: _.get(
      product,
      'viewExtensions.advertisedPrice',
      '',
    ).toLocaleString(),
    countries: _.get(product, 'viewExtensions.countries', []),
    description: _.get(product, 'description.longDescription', ''),
    destinationEnd: _.get(destinationEnd, 'title', ''),
    destinationStart:  _.get(destinationStart, 'title', ''),
    displayDestinations: _.map(tripDestinations, item => item.title),
    destinations: tripDestinations,
    detailsImage: _.get(product, 'viewExtensions.images.detailsImage')
      ? _.get(product, 'viewExtensions.images.detailsImage')
        ? _.get(product, 'viewExtensions.images.detailsImage.path')
        : _.get(product, 'viewExtensions.images.detailsImage')
      : _.get(product, 'viewExtensions.images.image', ''),
    duration: duration,
    famousRoutes: _.map(_.get(product, 'viewExtensions.famousRoutes', []), item => _.replace(item, 'Route_', '')),
    heroDestinationCount: isCityBreak
      ? _.size(_.without(uniqueDestinations, _.get(destinationStart, 'id'), _.get(destinationEnd, 'id')))
      : _.size(uniqueDestinations),
    heroImage: _.get(product, 'viewExtensions.images.heroImage'),
    heroImages: _.map(_.get(product, 'viewExtensions.images.heroImages') || []),
    isCityBreak,
    isGetaway: Boolean(_.find(vacationTypes, type => type.toLowerCase() === 'rail getaways')),
    isRailAndSail: Boolean(_.find(vacationTypes, type => type.toLowerCase() === 'rail and sail')),
    isRoundtrip: Boolean(_.find(vacationTypes, type => type.toLowerCase() === 'roundtrip vacations')),
    map:
      _.get(product, 'viewExtensions.images.mapImage.path') ||
      _.get(product, 'viewExtensions.images.mapImage'),
    marketingUrl: _.get(product, 'viewExtensions.url'),
    name: _.get(product, 'basicInfo.name'),
    offer: formatOffer(_.get(product, 'viewExtensions.offers', []), duration),
    offerImage: _.get(product, 'viewExtensions.images.offerImage'),
    preAccommodations: _.get(product, 'viewExtensions.preAccommodations'),
    postAccommodations: _.get(product, 'viewExtensions.postAccommodations'),
    regions: _.get(product, 'viewExtensions.region', []),
    reverseItinerary: _.get(product, 'viewExtensions.reverseItinerary.link'),
    roundTripUpgradePrice: _.get(product, 'viewExtensions.roundTripUpgrade'),
    softripBooking: _.get(product, 'viewExtensions.softripBooking'),
    relatedTrains: _.map(_.get(product, 'viewExtensions.relatedTrains'), (train) => ({
      id: _.get(train, 'trainId'),
      title: _.get(train, 'trainTitle'),
    })),
    tripItinerary,
    tripItineraryIsEmpty: !_.some(tripItinerary, item => item.description),
    tripHighlights: _.get(product, 'viewExtensions.highlights'),
    vacationTypes,
    whatsIncluded: _.get(product, 'viewExtensions.whatsIncluded'),
  }
}

export function formatRoomDetails(rooms) {
  let roomParams = ''
  _.forEach(rooms, (room) => {
    const adults = _.join(_.get(room, 'adults', []), '')
    let children = ''
    _.forEach(_.get(room, 'children', []), (age) => {
      children = `${children}C${age}`
    })
    roomParams = `${roomParams}${roomParams ? '/' : ''}${adults}${children}`
  })
  return { SearchMask: roomParams }
}

export function formatGetawayRoomDetails(rooms) {
  let roomParams = {}
  roomParams['rooms'] = `${rooms.length}`
  _.forEach(rooms, (room, index) => {
    if (!_.isEmpty(_.get(room, 'adults', []))) {
      roomParams[`rm${index + 1}adts`] = `${_.get(room, 'adults', []).length}`
    }
    if (!_.isEmpty(_.get(room, 'children', []))) {
      roomParams[`rm${index + 1}children`] = `${
        _.get(room, 'children', []).length
      }`
    }
    if (!_.isEmpty(_.get(room, 'children', []))) {
      roomParams[`rm${index + 1}childrenages`] = _.join(
        _.get(room, 'children', []),
        '-'
      )
    }
  })
  return roomParams
}

export function formatRoomDetailsForDeepLinking(rooms, isSoftripGetaway) {
  return isSoftripGetaway
    ? formatGetawayRoomDetails(rooms)
    : formatRoomDetails(rooms)
}
